import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Users, Car, Map, Calendar, BarChart3, Plug, TrendingUp, DollarSign, Clock } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const navItems = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Conductores", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users },
  { to: "/admin/integraciones", label: "Integraciones", icon: Plug },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];
const bottomNav = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Choferes", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];

const STATUS_LABELS = { pendiente_pago: "Pendiente", confirmado: "Confirmado", completado: "Completado", cancelado: "Cancelado" };

const AdminDashboard = () => {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState([]);
  const [activeDrivers, setActiveDrivers] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [dashboard, reservations, drivers] = await Promise.all([
          api.get("/api/admin/dashboard"),
          api.get("/api/admin/reservas"),
          api.get("/api/admin/conductores"),
        ]);
        setKpis(dashboard.data);
        setRecent(reservations.data.slice(0, 5));
        setActiveDrivers(drivers.data.filter((d) => d.user.is_active).slice(0, 5));
      } catch {
        setError("No se pudieron cargar los datos del panel.");
      }
    })();
  }, []);

  return (
    <PanelShell role="Admin" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} userMeta="Administrador · LEP HQ" testId="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Operaciones · Hoy</p>
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Panel operativo</h1>
          </div>
        </div>

        {error && <p className="mt-4 text-xs text-red-400 font-body" data-testid="admin-dashboard-error">{error}</p>}

        {/* KPIs */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI icon={TrendingUp} label="Viajes hoy" value={kpis ? kpis.trips_today : "—"} testId="kpi-trips" />
          <KPI icon={DollarSign} label="Ingresos hoy" value={kpis ? `S/ ${kpis.revenue_today}` : "—"} testId="kpi-revenue" />
          <KPI icon={Car} label="Conductores activos" value={kpis ? kpis.active_drivers : "—"} testId="kpi-drivers" />
          <KPI icon={Clock} label="Pendientes" value={kpis ? kpis.pending_reservations : "—"} testId="kpi-pending" negative />
        </div>

        {/* Live tables */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden" data-testid="recent-reservations">
            <div className="p-5 border-b border-[#2E2F33] flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">En vivo</p>
                <h3 className="font-display font-bold text-sm uppercase tracking-wide">Reservas recientes</h3>
              </div>
              <Link to="/admin/reservas" className="text-xs text-[#C9A227] uppercase tracking-wider font-display">Ver todas</Link>
            </div>
            <ul className="divide-y divide-[#2E2F33]">
              {recent.map((r) => (
                <li key={r.id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <StatusBadge status={STATUS_LABELS[r.status]} />
                      <span className="text-[10px] uppercase tracking-wider text-[#A0A0A5] font-display">{r.modality}</span>
                    </div>
                    <p className="text-sm text-[#F5F5F5] font-body truncate">{r.contact_name}</p>
                  </div>
                  <span className="text-sm font-display font-bold text-[#C9A227] shrink-0">S/ {r.price_total}</span>
                </li>
              ))}
              {recent.length === 0 && <li className="px-5 py-6 text-center text-xs text-[#A0A0A5] font-body">Sin reservas todavía.</li>}
            </ul>
          </div>

          <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden" data-testid="active-drivers">
            <div className="p-5 border-b border-[#2E2F33] flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Flota</p>
                <h3 className="font-display font-bold text-sm uppercase tracking-wide">Conductores activos</h3>
              </div>
              <Link to="/admin/conductores" className="text-xs text-[#C9A227] uppercase tracking-wider font-display">Gestionar</Link>
            </div>
            <ul className="divide-y divide-[#2E2F33]">
              {activeDrivers.map((d) => {
                const activeVehicle = d.vehicles.find((v) => v.is_active);
                return (
                  <li key={d.user.id} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/40 flex items-center justify-center text-[#C9A227] font-display font-bold text-xs">
                        {d.user.full_name.split(" ").map((s) => s[0]).join("").slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-body text-[#F5F5F5]">{d.user.full_name}</p>
                        <p className="text-[10px] text-[#A0A0A5] truncate">
                          {activeVehicle ? `${activeVehicle.brand} ${activeVehicle.model} · ${activeVehicle.plate}` : "Sin vehículo activo"}
                        </p>
                      </div>
                    </div>
                    <StatusBadge status="Activo" />
                  </li>
                );
              })}
              {activeDrivers.length === 0 && <li className="px-5 py-6 text-center text-xs text-[#A0A0A5] font-body">Sin conductores activos.</li>}
            </ul>
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

const KPI = ({ icon: Icon, label, value, testId, negative }) => (
  <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-5 relative overflow-hidden" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-lg border border-[#C9A227]/30 flex items-center justify-center">
        <Icon size={16} className={negative ? "text-amber-400" : "text-[#C9A227]"} />
      </div>
    </div>
    <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display">{label}</p>
    <p className="font-display font-bold text-2xl lg:text-3xl mt-1">{value}</p>
  </div>
);

export default AdminDashboard;
